import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useMemo, useState } from "react";
import languagesService from "services/languages-service";
import LanguagesEditNewDialog from "./LanguagesEditNewDialog";
import { useLanguage } from "contexts/language.context";

export default function AllLanguages({ permissions }) {
  const { label } = useLanguage();
  const [languages, setLanguages] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [refreshSignal, setRefreshSignal] = useState(null);
  const [rows, setRows] = useState([]);

  const [newDialogIsOpen, setNewDialogIsOpen] = useState(false);
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [alertIsOpen, setAlertIsOpen] = useState(false);

  const columns = [
    {
      Header: label("name-singular"),
      accessor: "description",
    },
    {
      Header: label("code-singular"),
      accessor: "code",
    },
    {
      Header: label("direction-singular"),
      accessor: "direction",
      width: "100%",
    },
  ];

  if (permissions.update || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
      width: "100%",
    });
  }

  useEffect(() => {
    updateRows();
  }, [languages]);

  async function handleAddLanguage(newElement) {
    const [response, data] = await languagesService.createLanguage(newElement);
    if (response.ok) {
      setRefreshSignal(Math.random());
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateLanguage(newElement) {
    const [response, data] = await languagesService.updateLanguage(
      newElement.id,
      newElement
    );
    if (response.ok) {
      setRefreshSignal(Math.random());
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteLanguage(id) {
    const [response, data] = await languagesService.removeLanguage(id);
    if (response.ok) {
      setRefreshSignal(Math.random());
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  function updateRows() {
    setRows(
      languages?.length > 0
        ? languages.map((language) => {
            return {
              description: (
                <MDTypography variant="inherit">{language.name}</MDTypography>
              ),

              code: (
                <MDTypography variant="inherit">{language.id}</MDTypography>
              ),

              direction: (
                <MDTypography variant="inherit">
                  {language.rtl
                    ? label("Right to left")
                    : label("Left to right")}
                </MDTypography>
              ),

              actions: (
                <MDBox
                  display="flex"
                  flexDirection="row"
                  justifyContent="end"
                  alignItems="center"
                >
                  {permissions.update && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditDialogIsOpen(Math.random());
                        setCurrentLanguage(language);
                      }}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  )}
                  {false && permissions.delete && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAlertIsOpen(Math.random());
                        setCurrentLanguage(language);
                      }}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  )}
                </MDBox>
              ),
            };
          })
        : languages?.length === 0
          ? [
              {
                description: label("no-languages-were-found"),
              },
            ]
          : [
              {
                description: label("loading"),
              },
            ]
    );
  }

  return (
    <>
      <AlertDialog
        open={alertIsOpen}
        setOpen={setAlertIsOpen}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          await handleDeleteLanguage(currentLanguage.id);
        }}
      />
      <LanguagesEditNewDialog
        isOpen={newDialogIsOpen}
        onSubmit={handleAddLanguage}
      />
      <LanguagesEditNewDialog
        type="edit"
        isOpen={editDialogIsOpen}
        onSubmit={handleUpdateLanguage}
        data={currentLanguage}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("language-plural")}{" "}
                  {permissions.get ? `(${languages?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      setNewDialogIsOpen(Math.random());
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-language-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-languages"
                      internalLoading={!Boolean(languages)}
                      fullWidth
                      setData={setLanguages}
                      onSearch={async (request, callback) => {
                        callback(
                          await languagesService.getMultipleLanguages(request)
                        );
                      }}
                      refreshSignal={refreshSignal}
                      label={label("search-languages-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(languages)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
